import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
  AddRouteParams,
  AddRouteResponse,
  AddRouterInterfaceParams,
  AddRouterInterfaceResponse,
  CreateFirewallParams,
  CreateFirewallResponse,
  CreateFirewallRuleParams,
  CreateFirewallRuleResponse,
  CreateFloatingIPParams,
  CreateFloatingIPResponse,
  CreateNetworkInterfaceParams,
  CreateNetworkInterfaceResponse,
  CreateNetworkParams,
  CreateNetworkResponse,
  CreateRouterParams,
  CreateRouterResponse,
  CreateSubnetParams,
  CreateSubnetResponse,
  DeleteFirewallParams,
  DeleteFirewallResponse,
  DeleteFirewallRuleParams,
  DeleteFirewallRuleResponse,
  DeleteFloatingIPParams,
  DeleteFloatingIPResponse,
  DeleteNetworkInterfaceParams,
  DeleteNetworkInterfaceResponse,
  DeleteNetworkParams,
  DeleteNetworkResponse,
  DeleteRouteParams,
  DeleteRouteResponse,
  DeleteRouterInterfaceParams,
  DeleteRouterInterfaceResponse,
  DeleteRouterParams,
  DeleteRouterResponse,
  DeleteSubnetParams,
  DeleteSubnetResponse,
  EditInterfaceFirewallsParams,
  EditInterfaceFirewallsResponse,
  EditNetworkInterfaceParams,
  EditNetworkInterfaceResponse,
  Firewall,
  FirewallRule,
  FloatingIP,
  GetFirewallParams,
  GetFirewallResponse,
  GetFirewallRuleParams,
  GetFirewallRuleResponse,
  GetFirewallRulesParams,
  GetFirewallRulesResponse,
  GetFirewallsParams,
  GetFirewallsResponse,
  GetFloatingIPsParams,
  GetFloatingIPsResponse,
  GetInterfacesParams,
  GetInterfacesResponse,
  GetNetworkInterfacesParams,
  GetNetworkInterfacesResponse,
  GetNetworkLimitsParams,
  GetNetworkLimitsResponse,
  GetNetworkParams,
  GetNetworkResponse,
  GetNetworksParams,
  GetNetworksResponse,
  GetNetworkSubnetsParams,
  GetNetworkSubnetsResponse,
  GetRouterInterfacesParams,
  GetRouterInterfacesResponse,
  GetRouterParams,
  GetRouterResponse,
  GetRoutersParams,
  GetRoutersResponse,
  GetSubnetParams,
  GetSubnetResponse,
  GetSubnetsParams,
  GetSubnetsResponse,
  Interface,
  Network,
  NetworkLimits,
  Router,
  Subnet,
  UpdateFirewallParams,
  UpdateFirewallResponse,
  UpdateFloatingIPParams,
  UpdateFloatingIPResponse,
  UpdateNetworkParams,
  UpdateNetworkResponse,
  UpdateRouterParams,
  UpdateRouterResponse,
  UpdateRouterStateParams,
  UpdateRouterStateResponse,
  UpdateSubnetParams,
  UpdateSubnetResponse
} from "./types";

export type NetworksState = {
  networks: Network[] | null;
  network: Network | null;
  floatingIP: FloatingIP | null;
  floatingIPs: FloatingIP[] | null;
  areFloatingIPsLoading: boolean;
  isFloatingIPCreating: boolean;
  isFloatingIPUpdating: boolean;
  isFloatingIPDeleting: boolean;
  routers: Router[] | null;
  router: Router | null;
  interfaces: Interface[] | null;
  routerInterfaces: Interface[] | null;
  networkInterfaces: Interface[] | null;
  areNetworkInterfacesLoading: boolean;
  isNetworkInterfaceEditing: boolean;
  isNetworkInterfaceCreating: boolean;
  isNetworkInterfaceDeleting: boolean;
  isNetworkLoading: boolean;
  areNetworksLoading: boolean;
  isNetworkCreating: boolean;
  isNetworkUpdating: boolean;
  isNetworkDeleting: boolean;
  isInterfaceFirewallsEditing: boolean;
  areRoutersLoading: boolean;
  isRouterCreating: boolean;
  isRouterUpdating: boolean;
  isRouterStateUpdating: boolean;
  isRouterDeleting: boolean;
  isRouterLoading: boolean;
  areInterfacesLoading: boolean;
  areRouterInterfacesLoading: boolean;
  isRouterInterfaceAdding: boolean;
  isRouterInterfaceDeleting: boolean;
  networkLimits: NetworkLimits | null;
  areNetworkLimitsLoading: boolean;
  subnets: Subnet[] | null;
  networkSubnets: Subnet[] | null;
  subnet: Subnet | null;
  isSubnetLoading: boolean;
  areSubnetsLoading: boolean;
  isSubnetCreating: boolean;
  isSubnetUpdating: boolean;
  isSubnetDeleting: boolean;
  firewalls: Firewall[] | null;
  firewall: Firewall | null;
  isFirewallLoading: boolean;
  areFirewallsLoading: boolean;
  isFirewallCreating: boolean;
  isFirewallUpdating: boolean;
  isFirewallDeleting: boolean;
  firewallRules: FirewallRule[] | null;
  firewallRule: FirewallRule | null;
  isFirewallRuleLoading: boolean;
  areFirewallRulesLoading: boolean;
  isFirewallRuleCreating: boolean;
  isFirewallRuleDeleting: boolean;
  isRouteAdding: boolean;
  isRouteDeleting: boolean;
  areNetworkSubnetsLoading: boolean;
};

const initialState: NetworksState = {
  networks: null,
  network: null,
  floatingIP: null,
  floatingIPs: null,
  areFloatingIPsLoading: false,
  isFloatingIPCreating: false,
  isFloatingIPUpdating: false,
  isFloatingIPDeleting: false,
  routers: null,
  router: null,
  interfaces: null,
  routerInterfaces: null,
  networkInterfaces: null,
  areNetworkInterfacesLoading: false,
  isNetworkInterfaceEditing: false,
  isNetworkInterfaceCreating: false,
  isNetworkInterfaceDeleting: false,
  isNetworkLoading: false,
  areNetworksLoading: false,
  isNetworkCreating: false,
  isNetworkUpdating: false,
  isNetworkDeleting: false,
  isRouterLoading: false,
  areRoutersLoading: false,
  isRouterCreating: false,
  isRouterUpdating: false,
  isRouterStateUpdating: false,
  isRouterDeleting: false,
  areInterfacesLoading: false,
  areRouterInterfacesLoading: false,
  isRouterInterfaceAdding: false,
  isRouterInterfaceDeleting: false,
  isInterfaceFirewallsEditing: false,
  networkLimits: null,
  areNetworkLimitsLoading: false,
  subnets: null,
  networkSubnets: null,
  subnet: null,
  isSubnetLoading: false,
  areSubnetsLoading: false,
  isSubnetCreating: false,
  isSubnetUpdating: false,
  isSubnetDeleting: false,
  firewalls: null,
  firewall: null,
  isFirewallLoading: false,
  areFirewallsLoading: false,
  isFirewallCreating: false,
  isFirewallUpdating: false,
  isFirewallDeleting: false,
  firewallRules: null,
  firewallRule: null,
  isFirewallRuleLoading: false,
  areFirewallRulesLoading: false,
  isFirewallRuleCreating: false,
  isFirewallRuleDeleting: false,
  isRouteAdding: false,
  isRouteDeleting: false,
  areNetworkSubnetsLoading: false
};

const networksSlice = createSlice({
  name: "networks",
  initialState,
  reducers: {
    // Networks
    getNetworkRequest: (state, action: PayloadAction<GetNetworkParams>) => {
      state.isNetworkLoading = true;
    },
    getNetworkSuccess: (state, action: PayloadAction<GetNetworkResponse>) => {
      state.network = action.payload;
      state.isNetworkLoading = false;
    },
    getNetworkFailure: (
      state,
      action: PayloadAction<{ params: GetNetworkParams; error: string }>
    ) => {
      state.isNetworkLoading = false;
    },

    getNetworksRequest: (state, action: PayloadAction<GetNetworksParams>) => {
      state.areNetworksLoading = true;
    },
    getNetworksSuccess: (state, action: PayloadAction<GetNetworksResponse>) => {
      state.networks = action.payload;
      state.areNetworksLoading = false;
    },
    getNetworksFailure: (
      state,
      action: PayloadAction<{ params: GetNetworksParams; error: string }>
    ) => {
      state.areNetworksLoading = false;
    },

    createNetworkRequest: (
      state,
      action: PayloadAction<CreateNetworkParams>
    ) => {
      state.isNetworkCreating = true;
    },
    createNetworkSuccess: (
      state,
      action: PayloadAction<CreateNetworkResponse>
    ) => {
      state.isNetworkCreating = false;
    },
    createNetworkFailure: (
      state,
      action: PayloadAction<{ params: CreateNetworkParams; error: string }>
    ) => {
      state.isNetworkCreating = false;
    },

    updateNetworkRequest: (
      state,
      action: PayloadAction<UpdateNetworkParams>
    ) => {
      state.isNetworkUpdating = true;
    },
    updateNetworkSuccess: (
      state,
      action: PayloadAction<UpdateNetworkResponse>
    ) => {
      state.isNetworkUpdating = false;
    },
    updateNetworkFailure: (
      state,
      action: PayloadAction<{ params: UpdateNetworkParams; error: string }>
    ) => {
      state.isNetworkUpdating = false;
    },

    deleteNetworkRequest: (
      state,
      action: PayloadAction<DeleteNetworkParams>
    ) => {
      state.isNetworkDeleting = true;
    },
    deleteNetworkSuccess: (
      state,
      action: PayloadAction<DeleteNetworkResponse>
    ) => {
      state.isNetworkDeleting = false;
      state.network = null;
    },
    deleteNetworkFailure: (
      state,
      action: PayloadAction<{ params: DeleteNetworkParams; error: string }>
    ) => {
      state.isNetworkDeleting = false;
    },

    // Floating IPs
    getFloatingIPsRequest: (
      state,
      action: PayloadAction<GetFloatingIPsParams>
    ) => {
      state.areFloatingIPsLoading = true;
    },
    getFloatingIPsSuccess: (
      state,
      action: PayloadAction<GetFloatingIPsResponse>
    ) => {
      state.floatingIPs = action.payload;
      state.areFloatingIPsLoading = false;
    },
    getFloatingIPsFailure: (
      state,
      action: PayloadAction<{ params: GetFloatingIPsParams; error: string }>
    ) => {
      state.areFloatingIPsLoading = false;
    },

    createFloatingIPRequest: (
      state,
      action: PayloadAction<CreateFloatingIPParams>
    ) => {
      state.isFloatingIPCreating = true;
    },
    createFloatingIPSuccess: (
      state,
      action: PayloadAction<CreateFloatingIPResponse>
    ) => {
      state.isFloatingIPCreating = false;
    },
    createFloatingIPFailure: (
      state,
      action: PayloadAction<{ params: CreateFloatingIPParams; error: string }>
    ) => {
      state.isFloatingIPCreating = false;
    },

    updateFloatingIPRequest: (
      state,
      action: PayloadAction<UpdateFloatingIPParams>
    ) => {
      state.isFloatingIPUpdating = true;
    },
    updateFloatingIPSuccess: (
      state,
      action: PayloadAction<UpdateFloatingIPResponse>
    ) => {
      state.isFloatingIPUpdating = false;
    },
    updateFloatingIPFailure: (
      state,
      action: PayloadAction<{ params: UpdateFloatingIPParams; error: string }>
    ) => {
      state.isFloatingIPUpdating = false;
    },

    deleteFloatingIPRequest: (
      state,
      action: PayloadAction<DeleteFloatingIPParams>
    ) => {
      state.isFloatingIPDeleting = true;
    },
    deleteFloatingIPSuccess: (
      state,
      action: PayloadAction<DeleteFloatingIPResponse>
    ) => {
      state.isFloatingIPDeleting = false;
    },
    deleteFloatingIPFailure: (
      state,
      action: PayloadAction<{ params: DeleteFloatingIPParams; error: string }>
    ) => {
      state.isFloatingIPDeleting = false;
    },

    // Routers
    getRouterRequest: (state, action: PayloadAction<GetRouterParams>) => {
      state.isRouterLoading = true;
    },
    getRouterSuccess: (state, action: PayloadAction<GetRouterResponse>) => {
      state.router = action.payload;
      state.isRouterLoading = false;
    },
    getRouterFailure: (
      state,
      action: PayloadAction<{ params: GetRouterParams; error: string }>
    ) => {
      state.isRouterLoading = false;
    },

    getRoutersRequest: (state, action: PayloadAction<GetRoutersParams>) => {
      state.areRoutersLoading = true;
    },
    getRoutersSuccess: (state, action: PayloadAction<GetRoutersResponse>) => {
      state.routers = action.payload;
      state.areRoutersLoading = false;
    },
    getRoutersFailure: (
      state,
      action: PayloadAction<{ params: GetRoutersParams; error: string }>
    ) => {
      state.areRoutersLoading = false;
    },

    createRouterRequest: (state, action: PayloadAction<CreateRouterParams>) => {
      state.isRouterCreating = true;
    },
    createRouterSuccess: (
      state,
      action: PayloadAction<CreateRouterResponse>
    ) => {
      state.isRouterCreating = false;
    },
    createRouterFailure: (
      state,
      action: PayloadAction<{ params: CreateRouterParams; error: string }>
    ) => {
      state.isRouterCreating = false;
    },

    updateRouterRequest: (state, action: PayloadAction<UpdateRouterParams>) => {
      state.isRouterUpdating = true;
    },
    updateRouterSuccess: (
      state,
      action: PayloadAction<UpdateRouterResponse>
    ) => {
      state.isRouterUpdating = false;
    },
    updateRouterFailure: (
      state,
      action: PayloadAction<{ params: UpdateRouterParams; error: string }>
    ) => {
      state.isRouterUpdating = false;
    },

    updateRouterStateRequest: (
      state,
      action: PayloadAction<UpdateRouterStateParams>
    ) => {
      state.isRouterStateUpdating = true;
    },
    updateRouterStateSuccess: (
      state,
      action: PayloadAction<UpdateRouterStateResponse>
    ) => {
      state.isRouterStateUpdating = false;
    },
    updateRouterStateFailure: (
      state,
      action: PayloadAction<{ params: UpdateRouterStateParams; error: string }>
    ) => {
      state.isRouterStateUpdating = false;
    },

    deleteRouterRequest: (state, action: PayloadAction<DeleteRouterParams>) => {
      state.isRouterDeleting = true;
    },
    deleteRouterSuccess: (
      state,
      action: PayloadAction<DeleteRouterResponse>
    ) => {
      state.isRouterDeleting = false;
      state.router = null;
    },
    deleteRouterFailure: (
      state,
      action: PayloadAction<{ params: DeleteRouterParams; error: string }>
    ) => {
      state.isRouterDeleting = false;
    },

    // Interfaces
    getInterfacesRequest: (
      state,
      action: PayloadAction<GetInterfacesParams>
    ) => {
      state.areInterfacesLoading = true;
    },
    getInterfacesSuccess: (
      state,
      action: PayloadAction<GetInterfacesResponse>
    ) => {
      state.interfaces = action.payload;
      state.areInterfacesLoading = false;
    },
    getInterfacesFailure: (
      state,
      action: PayloadAction<{ params: GetInterfacesParams; error: string }>
    ) => {
      state.areInterfacesLoading = false;
    },

    getRouterInterfacesRequest: (
      state,
      action: PayloadAction<GetRouterInterfacesParams>
    ) => {
      state.areRouterInterfacesLoading = true;
    },
    getRouterInterfacesSuccess: (
      state,
      action: PayloadAction<GetRouterInterfacesResponse>
    ) => {
      state.routerInterfaces = action.payload;
      state.areRouterInterfacesLoading = false;
    },
    getRouterInterfacesFailure: (
      state,
      action: PayloadAction<{
        params: GetRouterInterfacesParams;
        error: string;
      }>
    ) => {
      state.areRouterInterfacesLoading = false;
    },

    addRouterInterfaceRequest: (
      state,
      action: PayloadAction<AddRouterInterfaceParams>
    ) => {
      state.isRouterInterfaceAdding = true;
    },
    addRouterInterfaceSuccess: (
      state,
      action: PayloadAction<AddRouterInterfaceResponse>
    ) => {
      state.isRouterInterfaceAdding = false;
    },
    addRouterInterfaceFailure: (
      state,
      action: PayloadAction<{ params: AddRouterInterfaceParams; error: string }>
    ) => {
      state.isRouterInterfaceAdding = false;
    },

    deleteRouterInterfaceRequest: (
      state,
      action: PayloadAction<DeleteRouterInterfaceParams>
    ) => {
      state.isRouterInterfaceDeleting = true;
    },
    deleteRouterInterfaceSuccess: (
      state,
      action: PayloadAction<DeleteRouterInterfaceResponse>
    ) => {
      state.isRouterInterfaceDeleting = false;
    },
    deleteRouterInterfaceFailure: (
      state,
      action: PayloadAction<{
        params: DeleteRouterInterfaceParams;
        error: string;
      }>
    ) => {
      state.isRouterInterfaceDeleting = false;
    },

    editInterfaceFirewallsRequest: (
      state,
      action: PayloadAction<EditInterfaceFirewallsParams>
    ) => {
      state.isInterfaceFirewallsEditing = true;
    },
    editInterfaceFirewallsSuccess: (
      state,
      action: PayloadAction<EditInterfaceFirewallsResponse>
    ) => {
      state.isInterfaceFirewallsEditing = false;
    },
    editInterfaceFirewallsFailure: (
      state,
      action: PayloadAction<{
        params: EditInterfaceFirewallsParams;
        error: string;
      }>
    ) => {
      state.isInterfaceFirewallsEditing = false;
    },

    // Network interfaces
    getNetworkInterfacesRequest: (
      state,
      action: PayloadAction<GetNetworkInterfacesParams>
    ) => {
      state.areNetworkInterfacesLoading = true;
    },
    getNetworkInterfacesSuccess: (
      state,
      action: PayloadAction<GetNetworkInterfacesResponse>
    ) => {
      state.networkInterfaces = action.payload;
      state.areNetworkInterfacesLoading = false;
    },
    getNetworkInterfacesFailure: (
      state,
      action: PayloadAction<{
        params: GetNetworkInterfacesParams;
        error: string;
      }>
    ) => {
      state.areNetworkInterfacesLoading = false;
    },

    editNetworkInterfaceRequest: (
      state,
      action: PayloadAction<EditNetworkInterfaceParams>
    ) => {
      state.isNetworkInterfaceEditing = true;
    },
    editNetworkInterfaceSuccess: (
      state,
      action: PayloadAction<EditNetworkInterfaceResponse>
    ) => {
      state.isNetworkInterfaceEditing = false;
    },
    editNetworkInterfaceFailure: (
      state,
      action: PayloadAction<{
        params: EditNetworkInterfaceParams;
        error: string;
      }>
    ) => {
      state.isNetworkInterfaceEditing = false;
    },

    deleteNetworkInterfaceRequest: (
      state,
      action: PayloadAction<DeleteNetworkInterfaceParams>
    ) => {
      state.isNetworkInterfaceDeleting = true;
    },
    deleteNetworkInterfaceSuccess: (
      state,
      action: PayloadAction<DeleteNetworkInterfaceResponse>
    ) => {
      state.isNetworkInterfaceDeleting = false;
    },
    deleteNetworkInterfaceFailure: (
      state,
      action: PayloadAction<{
        params: DeleteNetworkInterfaceParams;
        error: string;
      }>
    ) => {
      state.isNetworkInterfaceDeleting = false;
    },

    createNetworkInterfaceRequest: (
      state,
      action: PayloadAction<CreateNetworkInterfaceParams>
    ) => {
      state.isNetworkInterfaceCreating = true;
    },
    createNetworkInterfaceSuccess: (
      state,
      action: PayloadAction<CreateNetworkInterfaceResponse>
    ) => {
      state.isNetworkInterfaceCreating = false;
    },
    createNetworkInterfaceFailure: (
      state,
      action: PayloadAction<{
        params: CreateNetworkInterfaceParams;
        error: string;
      }>
    ) => {
      state.isNetworkInterfaceCreating = false;
    },

    // Network limits
    getNetworkLimitsRequest: (
      state,
      action: PayloadAction<GetNetworkLimitsParams>
    ) => {
      state.areNetworkLimitsLoading = true;
    },
    getNetworkLimitsSuccess: (
      state,
      action: PayloadAction<GetNetworkLimitsResponse>
    ) => {
      state.networkLimits = action.payload;
      state.areNetworkLimitsLoading = false;
    },
    getNetworkLimitsFailure: (
      state,
      action: PayloadAction<{ params: GetNetworkLimitsParams; error: string }>
    ) => {
      state.areNetworkLimitsLoading = false;
    },

    // Subnets
    getSubnetRequest: (state, action: PayloadAction<GetSubnetParams>) => {
      state.isSubnetLoading = true;
    },
    getSubnetSuccess: (state, action: PayloadAction<GetSubnetResponse>) => {
      state.subnet = action.payload;
      state.isSubnetLoading = false;
    },
    getSubnetFailure: (
      state,
      action: PayloadAction<{ params: GetSubnetParams; error: string }>
    ) => {
      state.isSubnetLoading = false;
    },

    getSubnetsRequest: (state, action: PayloadAction<GetSubnetsParams>) => {
      state.areSubnetsLoading = true;
    },
    getSubnetsSuccess: (state, action: PayloadAction<GetSubnetsResponse>) => {
      state.subnets = action.payload;
      state.areSubnetsLoading = false;
    },
    getSubnetsFailure: (
      state,
      action: PayloadAction<{ params: GetSubnetsParams; error: string }>
    ) => {
      state.areSubnetsLoading = false;
    },

    getNetworkSubnetsRequest: (
      state,
      action: PayloadAction<GetNetworkSubnetsParams>
    ) => {
      state.areNetworkSubnetsLoading = true;
    },
    getNetworkSubnetsSuccess: (
      state,
      action: PayloadAction<GetNetworkSubnetsResponse>
    ) => {
      state.networkSubnets = action.payload;
      state.areNetworkSubnetsLoading = false;
    },
    getNetworkSubnetsFailure: (
      state,
      action: PayloadAction<{ params: GetNetworkSubnetsParams; error: string }>
    ) => {
      state.areNetworkSubnetsLoading = false;
    },

    createSubnetRequest: (state, action: PayloadAction<CreateSubnetParams>) => {
      state.isSubnetCreating = true;
    },
    createSubnetSuccess: (
      state,
      action: PayloadAction<CreateSubnetResponse>
    ) => {
      state.isSubnetCreating = false;
    },
    createSubnetFailure: (
      state,
      action: PayloadAction<{ params: CreateSubnetParams; error: string }>
    ) => {
      state.isSubnetCreating = false;
    },

    updateSubnetRequest: (state, action: PayloadAction<UpdateSubnetParams>) => {
      state.isSubnetUpdating = true;
    },
    updateSubnetSuccess: (
      state,
      action: PayloadAction<UpdateSubnetResponse>
    ) => {
      state.isSubnetUpdating = false;
    },
    updateSubnetFailure: (
      state,
      action: PayloadAction<{ params: UpdateSubnetParams; error: string }>
    ) => {
      state.isSubnetUpdating = false;
    },

    deleteSubnetRequest: (state, action: PayloadAction<DeleteSubnetParams>) => {
      state.isSubnetDeleting = true;
    },
    deleteSubnetSuccess: (
      state,
      action: PayloadAction<DeleteSubnetResponse>
    ) => {
      state.isSubnetDeleting = false;
    },
    deleteSubnetFailure: (
      state,
      action: PayloadAction<{ params: DeleteSubnetParams; error: string }>
    ) => {
      state.isSubnetDeleting = false;
    },

    // Firewalls
    getFirewallRequest: (state, action: PayloadAction<GetFirewallParams>) => {
      state.isFirewallLoading = true;
    },
    getFirewallSuccess: (state, action: PayloadAction<GetFirewallResponse>) => {
      state.firewall = action.payload;
      state.isFirewallLoading = false;
    },
    getFirewallFailure: (
      state,
      action: PayloadAction<{ params: GetFirewallParams; error: string }>
    ) => {
      state.isFirewallLoading = false;
    },

    getFirewallsRequest: (state, action: PayloadAction<GetFirewallsParams>) => {
      state.areFirewallsLoading = true;
    },
    getFirewallsSuccess: (
      state,
      action: PayloadAction<GetFirewallsResponse>
    ) => {
      state.firewalls = action.payload;
      state.areFirewallsLoading = false;
    },
    getFirewallsFailure: (
      state,
      action: PayloadAction<{ params: GetFirewallsParams; error: string }>
    ) => {
      state.areFirewallsLoading = false;
    },

    createFirewallRequest: (
      state,
      action: PayloadAction<CreateFirewallParams>
    ) => {
      state.isFirewallCreating = true;
    },
    createFirewallSuccess: (
      state,
      action: PayloadAction<CreateFirewallResponse>
    ) => {
      state.isFirewallCreating = false;
    },
    createFirewallFailure: (
      state,
      action: PayloadAction<{ params: CreateFirewallParams; error: string }>
    ) => {
      state.isFirewallCreating = false;
    },

    updateFirewallRequest: (
      state,
      action: PayloadAction<UpdateFirewallParams>
    ) => {
      state.isFirewallUpdating = true;
    },
    updateFirewallSuccess: (
      state,
      action: PayloadAction<UpdateFirewallResponse>
    ) => {
      state.isFirewallUpdating = false;
    },
    updateFirewallFailure: (
      state,
      action: PayloadAction<{ params: UpdateFirewallParams; error: string }>
    ) => {
      state.isFirewallUpdating = false;
    },

    deleteFirewallRequest: (
      state,
      action: PayloadAction<DeleteFirewallParams>
    ) => {
      state.isFirewallDeleting = true;
    },
    deleteFirewallSuccess: (
      state,
      action: PayloadAction<DeleteFirewallResponse>
    ) => {
      state.isFirewallDeleting = false;
      state.firewall = null;
    },
    deleteFirewallFailure: (
      state,
      action: PayloadAction<{ params: DeleteFirewallParams; error: string }>
    ) => {
      state.isFirewallDeleting = false;
    },

    // Firewall rules
    getFirewallRuleRequest: (
      state,
      action: PayloadAction<GetFirewallRuleParams>
    ) => {
      state.isFirewallRuleLoading = true;
    },
    getFirewallRuleSuccess: (
      state,
      action: PayloadAction<GetFirewallRuleResponse>
    ) => {
      state.firewallRule = action.payload;
      state.isFirewallRuleLoading = false;
    },
    getFirewallRuleFailure: (
      state,
      action: PayloadAction<{ params: GetFirewallRuleParams; error: string }>
    ) => {
      state.isFirewallRuleLoading = false;
    },

    getFirewallRulesRequest: (
      state,
      action: PayloadAction<GetFirewallRulesParams>
    ) => {
      state.areFirewallRulesLoading = true;
    },
    getFirewallRulesSuccess: (
      state,
      action: PayloadAction<GetFirewallRulesResponse>
    ) => {
      state.firewallRules = action.payload;
      state.areFirewallRulesLoading = false;
    },
    getFirewallRulesFailure: (
      state,
      action: PayloadAction<{ params: GetFirewallRulesParams; error: string }>
    ) => {
      state.areFirewallRulesLoading = false;
    },

    createFirewallRuleRequest: (
      state,
      action: PayloadAction<CreateFirewallRuleParams>
    ) => {
      state.isFirewallRuleCreating = true;
    },
    createFirewallRuleSuccess: (
      state,
      action: PayloadAction<CreateFirewallRuleResponse>
    ) => {
      state.isFirewallRuleCreating = false;
    },
    createFirewallRuleFailure: (
      state,
      action: PayloadAction<{ params: CreateFirewallRuleParams; error: string }>
    ) => {
      state.isFirewallRuleCreating = false;
    },

    deleteFirewallRuleRequest: (
      state,
      action: PayloadAction<DeleteFirewallRuleParams>
    ) => {
      state.isFirewallRuleDeleting = true;
    },
    deleteFirewallRuleSuccess: (
      state,
      action: PayloadAction<DeleteFirewallRuleResponse>
    ) => {
      state.isFirewallRuleDeleting = false;
      state.firewallRule = null;
    },
    deleteFirewallRuleFailure: (
      state,
      action: PayloadAction<{ params: DeleteFirewallRuleParams; error: string }>
    ) => {
      state.isFirewallRuleDeleting = false;
    },

    // Routes
    addRouteRequest: (state, action: PayloadAction<AddRouteParams>) => {
      state.isRouteAdding = true;
    },
    addRouteSuccess: (state, action: PayloadAction<AddRouteResponse>) => {
      state.isRouteAdding = false;
    },
    addRouteFailure: (
      state,
      action: PayloadAction<{ params: AddRouteParams; error: string }>
    ) => {
      state.isRouteAdding = false;
    },

    deleteRouteRequest: (state, action: PayloadAction<DeleteRouteParams>) => {
      state.isRouteDeleting = true;
    },
    deleteRouteSuccess: (state, action: PayloadAction<DeleteRouteResponse>) => {
      state.isRouteDeleting = false;
    },
    deleteRouteFailure: (
      state,
      action: PayloadAction<{ params: DeleteRouteParams; error: string }>
    ) => {
      state.isRouteDeleting = false;
    },

    // Clear helpers
    clearNetworkPorts: (state) => {
      state.networkInterfaces = null;
    },
    clearNetworkSubnets: (state) => {
      state.networkSubnets = null;
    },
    clear: () => initialState
  }
});

export const {
  getNetworkRequest,
  getNetworkSuccess,
  getNetworkFailure,
  getNetworksRequest,
  getNetworksSuccess,
  getNetworksFailure,
  createNetworkRequest,
  createNetworkSuccess,
  createNetworkFailure,
  updateNetworkRequest,
  updateNetworkSuccess,
  updateNetworkFailure,
  deleteNetworkRequest,
  deleteNetworkSuccess,
  deleteNetworkFailure,
  getFloatingIPsRequest,
  getFloatingIPsSuccess,
  getFloatingIPsFailure,
  createFloatingIPRequest,
  createFloatingIPSuccess,
  createFloatingIPFailure,
  updateFloatingIPRequest,
  updateFloatingIPSuccess,
  updateFloatingIPFailure,
  deleteFloatingIPRequest,
  deleteFloatingIPSuccess,
  deleteFloatingIPFailure,
  getRouterRequest,
  getRouterSuccess,
  getRouterFailure,
  getRoutersRequest,
  getRoutersSuccess,
  getRoutersFailure,
  createRouterRequest,
  createRouterSuccess,
  createRouterFailure,
  updateRouterRequest,
  updateRouterSuccess,
  updateRouterFailure,
  updateRouterStateRequest,
  updateRouterStateSuccess,
  updateRouterStateFailure,
  deleteRouterRequest,
  deleteRouterSuccess,
  deleteRouterFailure,
  getInterfacesRequest,
  getInterfacesSuccess,
  getInterfacesFailure,
  getRouterInterfacesRequest,
  getRouterInterfacesSuccess,
  getRouterInterfacesFailure,
  addRouterInterfaceRequest,
  addRouterInterfaceSuccess,
  addRouterInterfaceFailure,
  deleteRouterInterfaceRequest,
  deleteRouterInterfaceSuccess,
  deleteRouterInterfaceFailure,
  editInterfaceFirewallsRequest,
  editInterfaceFirewallsSuccess,
  editInterfaceFirewallsFailure,
  getNetworkInterfacesRequest,
  getNetworkInterfacesSuccess,
  getNetworkInterfacesFailure,
  editNetworkInterfaceRequest,
  editNetworkInterfaceSuccess,
  editNetworkInterfaceFailure,
  deleteNetworkInterfaceRequest,
  deleteNetworkInterfaceSuccess,
  deleteNetworkInterfaceFailure,
  createNetworkInterfaceRequest,
  createNetworkInterfaceSuccess,
  createNetworkInterfaceFailure,
  getNetworkLimitsRequest,
  getNetworkLimitsSuccess,
  getNetworkLimitsFailure,
  getSubnetRequest,
  getSubnetSuccess,
  getSubnetFailure,
  getSubnetsRequest,
  getSubnetsSuccess,
  getSubnetsFailure,
  getNetworkSubnetsRequest,
  getNetworkSubnetsSuccess,
  getNetworkSubnetsFailure,
  createSubnetRequest,
  createSubnetSuccess,
  createSubnetFailure,
  updateSubnetRequest,
  updateSubnetSuccess,
  updateSubnetFailure,
  deleteSubnetRequest,
  deleteSubnetSuccess,
  deleteSubnetFailure,
  getFirewallRequest,
  getFirewallSuccess,
  getFirewallFailure,
  getFirewallsRequest,
  getFirewallsSuccess,
  getFirewallsFailure,
  createFirewallRequest,
  createFirewallSuccess,
  createFirewallFailure,
  updateFirewallRequest,
  updateFirewallSuccess,
  updateFirewallFailure,
  deleteFirewallRequest,
  deleteFirewallSuccess,
  deleteFirewallFailure,
  getFirewallRuleRequest,
  getFirewallRuleSuccess,
  getFirewallRuleFailure,
  getFirewallRulesRequest,
  getFirewallRulesSuccess,
  getFirewallRulesFailure,
  createFirewallRuleRequest,
  createFirewallRuleSuccess,
  createFirewallRuleFailure,
  deleteFirewallRuleRequest,
  deleteFirewallRuleSuccess,
  deleteFirewallRuleFailure,
  addRouteRequest,
  addRouteSuccess,
  addRouteFailure,
  deleteRouteRequest,
  deleteRouteSuccess,
  deleteRouteFailure,
  clearNetworkPorts,
  clearNetworkSubnets,
  clear
} = networksSlice.actions;

export const { actions: networksActions } = networksSlice;

export const reducer = networksSlice.reducer;
